import { Badge, Select } from '@chopchop-se/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { StockMovementRow, StockMovementType } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime, formatQty } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

const MOVEMENT_TYPES: StockMovementType[] = [
  'receive',
  'putaway',
  'pick',
  'pack',
  'ship',
  'move',
  'adjust',
  'consume',
  'produce',
  'count',
  'palletize',
];

/** Movement types that take stock OUT of the warehouse, for the tint. */
const OUTBOUND = new Set<StockMovementType>(['pick', 'ship', 'consume']);
const INBOUND = new Set<StockMovementType>(['receive', 'produce']);

/**
 * The ledger browser — the traceability answer surface (MODULES.md M2).
 *
 * Every filter here exists because somebody has to answer a question with it:
 * "which restaurant got batch L-1234" (lot), "what did this scanner do at
 * 14:05" (device + time window), "what has touched A-01-1A today" (bin).
 */
export function MovementsPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const [searchParams] = useSearchParams();
  const { state, setState, setFilter } = useListState({ defaultSort: 'at', defaultDir: 'desc' });

  // Deep links from the pallet/lot pages arrive as ?f_lotId=… and are picked up
  // by useListState, so no extra wiring is needed here.
  const deepLinked = searchParams.has('f_lotId') || searchParams.has('f_palletId');

  const { data, isFetching, error } = useQuery({
    queryKey: warehouseKeys.movements(listQueryKey(state)),
    queryFn: () => warehouseApi.listMovements(state),
    placeholderData: keepPreviousData,
  });
  useLiveQuery(['stock'], warehouseKeys.movements());

  const columns: DataTableColumn<StockMovementRow>[] = [
    {
      id: 'at',
      header: t('warehouseCore:movements.at'),
      sortField: 'at',
      cell: (row) => <span className="whitespace-nowrap font-mono text-xs">{formatDateTime(row.at)}</span>,
    },
    {
      id: 'type',
      header: t('warehouseCore:movements.type'),
      sortField: 'type',
      cell: (row) => (
        <Badge tone={INBOUND.has(row.type) ? 'success' : OUTBOUND.has(row.type) ? 'danger' : 'neutral'}>
          {t(`warehouseCore:movements.types.${row.type}`)}
        </Badge>
      ),
    },
    {
      id: 'article',
      header: t('warehouseCore:movements.article'),
      sortField: 'articleNo',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-ink">{row.articleName}</p>
          <p className="font-mono text-xs text-ink-muted">{row.articleNo}</p>
        </div>
      ),
    },
    {
      id: 'lot',
      header: t('warehouseCore:movements.lot'),
      cell: (row) =>
        row.lotId ? (
          <Link to={`/warehouse/lots/${row.lotId}`} className="font-mono text-sm text-brand-600 hover:underline">
            {row.lotNo}
          </Link>
        ) : (
          '—'
        ),
      hideBelow: 'lg',
    },
    {
      id: 'pallet',
      header: t('warehouseCore:movements.pallet'),
      cell: (row) =>
        row.palletId ? (
          <Link
            to={`/warehouse/pallets/${row.palletId}`}
            className="font-mono text-sm text-brand-600 hover:underline"
          >
            {row.lpn}
          </Link>
        ) : (
          '—'
        ),
      hideBelow: 'xl',
    },
    {
      id: 'route',
      header: `${t('warehouseCore:movements.from')} → ${t('warehouseCore:movements.to')}`,
      cell: (row) => (
        <span className="whitespace-nowrap font-mono text-xs">
          {row.fromBinCode ?? <span className="text-ink-muted">{t('warehouseCore:movements.external')}</span>}
          {' → '}
          {row.toBinCode ?? <span className="text-ink-muted">{t('warehouseCore:movements.external')}</span>}
        </span>
      ),
    },
    {
      id: 'qty',
      header: t('warehouseCore:movements.quantity'),
      align: 'right',
      sortField: 'qtyBase',
      cell: (row) => <span className="font-mono">{formatQty(row.qtyBase)}</span>,
    },
    {
      id: 'user',
      header: t('warehouseCore:movements.user'),
      cell: (row) => row.userName ?? '—',
      hideBelow: 'lg',
    },
  ];

  return (
    <>
      <PageHeader
        title={t('warehouseCore:movements.title')}
        subtitle={t('warehouseCore:movements.subtitle')}
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        nextCursor={data?.nextCursor ?? null}
        loading={isFetching}
        error={error}
        searchPlaceholder={t('warehouseCore:movements.search')}
        emptyTitle={t('warehouseCore:movements.empty')}
        emptyDescription={
          deepLinked ? t('warehouseCore:movements.emptyHelp') : t('warehouseCore:movements.emptyHelp')
        }
        filters={
          <>
            <Select
              aria-label={t('warehouseCore:movements.type')}
              className="w-44"
              value={String(state.filters?.type ?? '')}
              options={[
                { value: '', label: t('warehouseCore:common.all') },
                ...MOVEMENT_TYPES.map((type) => ({
                  value: type,
                  label: t(`warehouseCore:movements.types.${type}`),
                })),
              ]}
              onChange={(event) => {
                setFilter('type', event.target.value || undefined);
              }}
            />
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              {t('warehouseCore:movements.fromDate')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1 text-sm text-ink"
                value={String(state.filters?.atFrom ?? '')}
                onChange={(event) => {
                  setFilter('atFrom', event.target.value || undefined);
                }}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-muted">
              {t('warehouseCore:movements.toDate')}
              <input
                type="date"
                className="rounded-lg border border-surface-border bg-surface px-2 py-1 text-sm text-ink"
                value={String(state.filters?.atTo ?? '')}
                onChange={(event) => {
                  setFilter('atTo', event.target.value || undefined);
                }}
              />
            </label>
          </>
        }
      />
    </>
  );
}
